// Notes — a free-text rich-text note about the dashboard, ported 1:1 from
// js/renderers-notes.js via the 04b bridge. needsPlayer:false: no player, no picker,
// no sub-controls. Rich text via a real contenteditable + document.execCommand
// (bold/italic/link + foreColor swatches), exactly as the prototype (no new
// dependency — execCommand is deprecated in spec but still works everywhere this
// targets). Save is a dummy flash: the content is already live in the element's
// `_noteContent` the instant it's typed, so Save only ever gives confirmation.
//
// Instance state lives on the module element as `_noteContent` / `_noteTitle`
// (getModState/setModState), so main.js's saveState/undo/presets capture it
// generically — same contract as every other converted module.
//
// The contenteditable is deliberately UNCONTROLLED: React never owns the editor's
// children (it would fight the caret on every keystroke). A ref seeds and re-syncs
// its innerHTML from `_noteContent` only when the two differ — i.e. on mount and on
// a programmatic restore (undo / a preset), never on ordinary typing. This mirrors
// renderers-notes.js's tail re-sync (its comment at :119).

import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainNotes } from "./explain.js";
import { LIVE } from "../live/flag.js";
import { useEffect, useRef, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from "react";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { NOTE_COLORS, SAVE_FLASH_MS } from "./notes-view.js";

// Focus the editor before running the command so execCommand acts on the real
// current selection (renderers-notes.js:34).
function execOnEditor(editor: HTMLElement, command: string, value?: string): void {
  editor.focus();
  document.execCommand(command, false, value);
}

export function NotesModule({ mod }: ProtoModuleProps) {
  // Seed the element's instance state (idempotent), exactly like the legacy top.
  if (typeof getModState<string>(mod, "_noteContent") !== "string") setModState(mod, "_noteContent", "");
  if (typeof getModState<string>(mod, "_noteTitle") !== "string") setModState(mod, "_noteTitle", "");

  const editorRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [saved, setSaved] = useState(false);

  // Re-sync the visible editor + title from the element state whenever they diverge
  // — the mount seed and the undo/preset restore. A no-op on ordinary keystrokes
  // (onInput already keeps `_noteContent`/`_noteTitle` current without a re-render),
  // so the caret is never disturbed. Runs after every render (no dep array), matching
  // the legacy renderer running this on every refreshModuleBody call.
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const content = getModState<string>(mod, "_noteContent") ?? "";
      if (editor.innerHTML !== content) editor.innerHTML = content;
    }
    const title = titleRef.current;
    if (title) {
      const t = getModState<string>(mod, "_noteTitle") ?? "";
      if (title.value !== t) title.value = t;
    }
  });

  const onTitleInput = (e: FormEvent<HTMLInputElement>) => {
    setModState(mod, "_noteTitle", e.currentTarget.value);
  };
  const onEditorInput = (e: FormEvent<HTMLDivElement>) => {
    setModState(mod, "_noteContent", e.currentTarget.innerHTML);
  };

  // mousedown-preventDefault (not click): stop the button stealing focus from the
  // contenteditable first, which would collapse the selection Bold/Italic/Link/colour
  // is meant to apply to (renderers-notes.js:73).
  const keepFocus = (e: ReactMouseEvent) => e.preventDefault();

  const runCommand = (cmd: "bold" | "italic" | "link") => {
    const editor = editorRef.current;
    if (!editor) return;
    if (cmd === "link") {
      const url = window.prompt("Link URL:", "https://");
      if (url) execOnEditor(editor, "createLink", url);
    } else {
      execOnEditor(editor, cmd);
    }
    setModState(mod, "_noteContent", editor.innerHTML);
  };

  const applyColor = (color: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    execOnEditor(editor, "foreColor", color);
    setModState(mod, "_noteContent", editor.innerHTML);
  };

  const onSave = () => {
    if (saved) return; // one flash at a time (legacy's dataset.savedFlash guard)
    setSaved(true);
    setTimeout(() => setSaved(false), SAVE_FLASH_MS);
  };

  const onCancel = () => {
    setModState(mod, "_noteContent", "");
    setModState(mod, "_noteTitle", "");
    if (editorRef.current) editorRef.current.innerHTML = "";
    if (titleRef.current) titleRef.current.value = "";
  };

  return (
    <div className="notes-wrap">
      {LIVE && <ModuleExplainer mod={mod} paragraphs={explainNotes()} />}
      <input
        type="text"
        className="notes-title"
        placeholder="Title…"
        autoComplete="off"
        spellCheck={false}
        ref={titleRef}
        onInput={onTitleInput}
      />
      <div className="notes-toolbar">
        <button
          type="button"
          className="notes-btn notes-btn-bold"
          title="Bold"
          data-cmd="bold"
          onMouseDown={keepFocus}
          onClick={() => runCommand("bold")}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="notes-btn notes-btn-italic"
          title="Italic"
          data-cmd="italic"
          onMouseDown={keepFocus}
          onClick={() => runCommand("italic")}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="notes-btn notes-btn-link"
          title="Add link"
          data-cmd="link"
          onMouseDown={keepFocus}
          onClick={() => runCommand("link")}
        >
          <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
        <span className="notes-toolbar-divider" />
        {NOTE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className="notes-color-swatch"
            title={c}
            data-color={c}
            style={{ background: c }}
            onMouseDown={keepFocus}
            onClick={() => applyColor(c)}
          />
        ))}
      </div>
      <div
        className="notes-editor"
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        data-placeholder="Write your notes about this dashboard…"
        ref={editorRef}
        onInput={onEditorInput}
      />
      <div className="notes-footer">
        <button type="button" className="notes-btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="notes-btn-save" onClick={onSave}>
          {saved ? "Saved ✓" : "Save"}
        </button>
      </div>
    </div>
  );
}
